import { triggerDownload } from '@/api/invoices';
import type { CompanyInfo, InvoiceDetail } from '@/types';

/**
 * PDF generation is *render-on-demand*, not part of the preview.
 *
 * The v1 app built a PDF eagerly on every detail-page render, so a status
 * change or a template switch cost a full re-layout. Here the preview is HTML
 * (see `InvoiceDocument.tsx`) and a PDF is only produced when someone asks for
 * a file.
 *
 * Two ways out:
 *
 *  - `printInvoice` hands the on-screen HTML to the browser's print pipeline.
 *    Zero bundle cost, but it goes through a dialog, and "Save as PDF" there
 *    stamps the browser's date/URL/title headers on every page.
 *  - `downloadInvoicePdf` draws the invoice with jsPDF (`invoicePdf.ts`) and
 *    saves it straight to disk as "Invoice <number> - <client>.pdf". jsPDF sits
 *    behind a dynamic `import()`, so it is never in the initial bundle. It uses
 *    the PDF built-in Helvetica: no `Font.register`, nothing to fetch.
 *
 * ── Server dependency ───────────────────────────────────────────────────────
 * The emailed attachment still has no renderer: `@react-pdf/renderer` cannot
 * run on Workers (see `apps/server/src/routes/app/pdf.ts`), and the
 * email/Inngest path calls a different, overdue-notice-only renderer at
 * `/api/generate-invoice-pdf`. `buildInvoicePdfBlob` is the seam for that.
 */

export interface PdfSource {
  invoice: InvoiceDetail;
  company: CompanyInfo | undefined;
  clientLabel: string;
}

/**
 * Print the invoice document that is already on screen. The `print:` variants
 * in `InvoiceDocument` plus the rules below hide the app chrome, so the user
 * gets the invoice alone at the right size.
 */
export function printInvoice() {
  document.body.classList.add('printing-invoice');
  const cleanup = () => {
    document.body.classList.remove('printing-invoice');
    window.removeEventListener('afterprint', cleanup);
  };
  window.addEventListener('afterprint', cleanup);
  window.print();
  // Safari does not always fire afterprint.
  setTimeout(cleanup, 2000);
}

/** "Invoice 1042 - Acme Co.pdf" — the characters file systems reject become spaces. */
export function invoicePdfFilename(invoice: Pick<InvoiceDetail, 'invoiceNumber' | 'clientName'>) {
  const name = [`Invoice ${invoice.invoiceNumber}`, invoice.clientName]
    .filter(Boolean)
    .join(' - ')
    .replace(/[\\/:*?"<>|\u0000-\u001f]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/[.\s]+$/, '')
    .trim();
  return `${name}.pdf`;
}

/** Build the invoice PDF in the browser and save it. No dialog. */
export async function downloadInvoicePdf(source: PdfSource) {
  const { renderInvoicePdf } = await import('./invoicePdf');
  triggerDownload(renderInvoicePdf(source), invoicePdfFilename(source.invoice));
}

/**
 * Seam for a server-rendered PDF binary (needed to attach one to an email).
 *
 * Not implemented client-side on purpose — see the note above. When the server
 * exposes `POST /api/invoices/:id/pdf -> application/pdf`, this becomes a one
 * line fetch and the email flow stops needing a client-side layout engine at
 * all.
 */
export async function buildInvoicePdfBlob(invoiceId: string): Promise<Blob> {
  const res = await fetch(
    `${import.meta.env.VITE_API_BASE_URL ?? ''}/api/invoices/${invoiceId}/pdf`,
    { credentials: 'include', headers: { Accept: 'application/pdf' } }
  );
  if (!res.ok) {
    throw new Error(
      'PDF rendering is not available yet — the server does not expose /api/invoices/:id/pdf.'
    );
  }
  return res.blob();
}
